import { snake } from 'kitsu-core';
import { BANNER, BEVERAGE_TYPES } from '../lib/constants';
import { getBaseURL } from '../lib/getBaseURL';
import Banner from '../models/Banner';
import Category from '../models/Category';
import Cocktail from '../models/Cocktail';
import Drink from '../models/Drink';
import PhotoCocktail from '../models/PhotoCocktail';
import { useConfigContext } from './useConfigContext';
import consumer from "../../../channels/consumer";

const baseURL = getBaseURL();
const REQUEST_TIMEOUT = 10000;

const joinUrl = (urlBase, path) => {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalizedBase = urlBase.endsWith('/') ? urlBase.slice(0, -1) : urlBase;
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

export default function useAPI() {
  const { drinkMenu, setDrinkMenu } = useConfigContext();

  const apiRequest = async (path, options = {}) => {
    const {
      method = 'GET',
      requestData,
      requestBaseURL = baseURL,
      headers = {},
    } = options;

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(joinUrl(requestBaseURL, path), {
        method,
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          ...headers,
        },
        body: requestData ? JSON.stringify(requestData) : undefined,
        signal: controller.signal,
      });

      if (!response.ok) {
        throw Error(`API: Request failed with status ${response.status}`);
      }

      if (response.status === 204) {
        return { data: null };
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('json')) {
        const data = await response.json();
        return { data };
      }

      return { data: null };
    } finally {
      clearTimeout(timeout);
    }
  };

  const createItem = async (item, category) => {
    const requestData = item.toRequest(drinkMenu, category);
    const res = await apiRequest(`${item.type}_items`, {
      method: 'POST',
      requestData,
    });

    if (!res.data) throw Error('API: No data in response');

    const { id } = res.data.data;

    if (item.type === BEVERAGE_TYPES.PHOTO_COCKTAIL) {
      const { image_path: imagePath, image_preview_path: imagePreviewPath } = res.data.data.attributes;
      if (imagePath && imagePreviewPath) {
        return new PhotoCocktail({ ...item, imagePath, imagePreviewPath, id });
      } else {
        return new PhotoCocktail({ ...item, id });
      }
    }

    if (item.type === BEVERAGE_TYPES.COCKTAIL) {
      return new Cocktail({ ...item, id });
    }

    if (item.type === BEVERAGE_TYPES.DRINK) {
      return new Drink({ ...item, id });
    }

    if (item.type === BANNER) {
      return new Banner({ ...item, id });
    }
  };

  const updateItem = async (beverage, category) => {
    const requestData = beverage.toRequest(drinkMenu, category);
    await apiRequest(`${beverage.type}_items/${beverage.id}`, {
      method: 'PATCH',
      requestData,
    });
  };

  const deleteItem = ({ id, type }) =>
    apiRequest(`${type}_items/${id}`, { method: 'DELETE' });

  const createCategory = async category => {
    const requestData = category.toRequest(drinkMenu);
    const res = await apiRequest(`${category.type}_menu_categories`, {
      method: 'POST',
      requestData,
    });

    if (!res.data) throw Error('API: No data in response');

    const { id } = res.data.data;
    return new Category({ ...category, id });
  };

  const updateCategory = async category => {
    const requestData = category.toRequest(drinkMenu);
    await apiRequest(`${category.type}_menu_categories/${category.id}`, {
      method: 'PATCH',
      requestData,
    });
  };

  const deleteCategory = ({ id, type }) =>
    apiRequest(`${type}_menu_categories/${id}`, { method: 'DELETE' });

  const updateDrinkMenuAttributes = async attributes => {
    attributes = Object.fromEntries(
      Object.entries(attributes).map(([key, value]) => [snake(key), value])
    );

    const requestData = {
      data: {
        type: 'drink_menu',
        attributes,
      },
    };

    const res = await apiRequest(`drink_menus/${drinkMenu.id}`, {
      method: 'PATCH',
      requestData,
    });

    return res;
  };

  const updateDrinkMenuLogo = async (blobId) => {
    const res = await updateDrinkMenuAttributes({logo: blobId});

    if (!res.data) throw Error('API: No data in response');

    setDrinkMenu(old => ({...old, logoPath: res.data.data.attributes.logo_path }));
  };

  const requestDrinkMenuPDF = async () => {
    await apiRequest(`drink_menus/${drinkMenu.id}.pdf`, { requestBaseURL: '/' });
  }

  const requestPrefilledDrinkMenu = async () => {
    const requestData = {
      data: {
        type: 'drink_menu',
        attributes: {
          prefill: 'jack_daniels',
        }
      }
    }

    await apiRequest(`drink_menus/${drinkMenu.id}?include=items,menu_categories`, {
      method: 'PATCH',
      requestData,
    });
  }

  return {
    createItem,
    updateItem,
    deleteItem,

    createCategory,
    updateCategory,
    deleteCategory,

    requestDrinkMenuPDF,

    requestPrefilledDrinkMenu,

    updateDrinkMenuAttributes,
    updateDrinkMenuLogo,
  };
}
